/**
 * TRUS-709: Admin → Messaging settings for member conversations.
 *
 *  - Who sees member conversations: everyone on the team (default) or each
 *    staff member's caseload (program caseload models, assignments, roles that
 *    see all members);
 *  - whether members see the name of the staff member who replied.
 *
 * GET / PATCH /api/messaging/settings (PATCH sends only the changed key; org
 * admins only). The settings belong to the ACTIVE organization (X-CBO-ID), so
 * a page about another org says to switch first. `useMessagingSettings`
 * returns null when the endpoint is not deployed: the caller hides the section.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import {
  memberInboxApi,
  type InboxMemberVisibility,
  type MessagingSettings,
} from '@/services/memberInboxApi';
import { useIsOrgAdmin } from '../channelActions';

export function messagingSettingsKey(): unknown[] {
  return ['messaging-settings', typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null];
}

/** The active org's messaging settings; `data === null` when the endpoint is missing. */
export function useMessagingSettings() {
  return useQuery({
    queryKey: messagingSettingsKey(),
    queryFn: () => memberInboxApi.getSettings(),
    staleTime: 60_000,
    retry: false,
  });
}

interface MemberInboxSettingsPanelProps {
  /** The org the surrounding page is about; settings apply to the active org. */
  organizationId?: number;
  /** Open program settings (the caseload model lives on each program). */
  onOpenProgramSettings?: () => void;
}

export function MemberInboxSettingsPanel({ organizationId, onOpenProgramSettings }: MemberInboxSettingsPanelProps) {
  const { t } = useAppTranslation();
  const qc = useQueryClient();
  const isOrgAdmin = useIsOrgAdmin();
  const { data: settings, isLoading, isError } = useMessagingSettings();
  const activeOrgId = Number(typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : NaN);
  const otherOrg = organizationId != null && Number.isFinite(activeOrgId) && organizationId !== activeOrgId;

  const save = useMutation({
    mutationFn: (patch: Partial<Pick<MessagingSettings, 'inbox_member_visibility' | 'show_staff_names_to_members'>>) =>
      memberInboxApi.updateSettings(patch),
    onSuccess: (next) => {
      qc.setQueryData(messagingSettingsKey(), next);
      qc.invalidateQueries({ queryKey: ['member-inbox'] });
      toast({ title: t('messaging.settings.saved', 'Messaging settings saved') });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (settings === null) return null;
  if (isError || !settings) {
    return (
      <p className="text-sm text-destructive" role="alert">
        {t('messaging.settings.loadFailed', 'Could not load messaging settings.')}
      </p>
    );
  }

  const canEdit = (settings.can_edit ?? isOrgAdmin) && !otherOrg;
  const visibility = settings.inbox_member_visibility;
  const openPrograms =
    onOpenProgramSettings ??
    (() => window.dispatchEvent(new CustomEvent('changeTab', { detail: 'programs' })));

  const option = (value: InboxMemberVisibility, title: string, hint: string) => (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
        visibility === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
        !canEdit && 'cursor-default opacity-80',
      )}
    >
      <input
        type="radio"
        name="inbox-member-visibility"
        value={value}
        checked={visibility === value}
        disabled={!canEdit || save.isPending}
        onChange={() => save.mutate({ inbox_member_visibility: value })}
        className="mt-1"
        data-testid={`inbox-visibility-${value}`}
      />
      <span>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </label>
  );

  return (
    <section className="space-y-6" data-testid="member-inbox-settings">
      <div>
        <h2 className="text-lg font-semibold">{t('messaging.settings.title', 'Member Inbox')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('messaging.settings.subtitle', 'Who sees member conversations, and what members see about who replied.')}
        </p>
      </div>

      {otherOrg && (
        <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100" data-testid="inbox-settings-other-org">
          {t('messaging.settings.otherOrg', 'These settings belong to the organization you are working in. Switch to this organization to change them.')}
        </p>
      )}
      {!otherOrg && !canEdit && (
        <p className="text-xs text-muted-foreground" data-testid="inbox-settings-read-only">
          {t('messaging.settings.readOnly', 'Only organization admins can change these settings.')}
        </p>
      )}

      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-semibold">
          {t('messaging.settings.visibilityLabel', 'Who sees member conversations')}
        </legend>
        {option(
          'team',
          t('messaging.settings.visibilityTeam', 'Everyone on the team (default)'),
          t('messaging.settings.visibilityTeamHint', 'Every staff member of the organization sees and can answer every member conversation.'),
        )}
        {option(
          'caseload',
          t('messaging.settings.visibilityCaseload', "Each staff member's caseload"),
          t('messaging.settings.visibilityCaseloadHint', 'Staff see the conversations of members they work with. Admins see all of them.'),
        )}
        <div className="rounded-md bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="inbox-caseload-explainer">
          <p className="mb-1 font-medium text-foreground">
            {t('messaging.settings.caseloadRulesTitle', 'In caseload mode, a staff member sees a member conversation when:')}
          </p>
          <ul className="list-disc space-y-0.5 pl-4">
            <li>{t('messaging.settings.caseloadRuleProgram', "the member is in a program with a shared caseload that they work in (programs with an individual caseload count only the members assigned to them);")}</li>
            <li>{t('messaging.settings.caseloadRuleAssigned', 'they are assigned to the member (case manager or primary coordinator);')}</li>
            <li>{t('messaging.settings.caseloadRuleRole', 'their role can "see all members";')}</li>
            <li>{t('messaging.settings.caseloadRuleOwner', 'they own the conversation, or replied in it in the last 90 days.')}</li>
          </ul>
          <p className="mt-1">
            {t('messaging.settings.caseloadUnrouted', 'A conversation nobody but an admin can see shows to admins under "Unrouted", so no member message goes unseen.')}
          </p>
          <Button variant="link" size="sm" className="mt-1 h-auto gap-1 p-0 text-xs" onClick={openPrograms} data-testid="inbox-settings-programs-link">
            {t('messaging.settings.programLink', 'Set each program’s caseload model in program settings')}
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </fieldset>

      <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
        <div>
          <label htmlFor="show-staff-names" className="block text-sm font-medium">
            {t('messaging.settings.staffNames', 'Show staff names to members')}
          </label>
          <p className="text-xs text-muted-foreground">
            {t('messaging.settings.staffNamesHint', 'Members see the first name and last initial of the staff member who replied. Off: they see the organization’s name.')}
          </p>
        </div>
        <Switch
          id="show-staff-names"
          checked={settings.show_staff_names_to_members}
          disabled={!canEdit || save.isPending}
          onCheckedChange={(checked) => save.mutate({ show_staff_names_to_members: checked })}
          data-testid="inbox-staff-names"
        />
      </div>

      {save.isError && (
        <p className="text-sm text-destructive" role="alert">
          {t('messaging.settings.saveFailed', 'Could not save messaging settings.')}
        </p>
      )}
    </section>
  );
}

export default MemberInboxSettingsPanel;
